import { useEffect } from "react";
import { ArrowLeft, ShieldCheck, Clock, Lock } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo, useHashScroll } from "@/lib/navigation";

export const FORMS = {
  "client-authorisation": { ids: { en: "260990929441062", zh: "261018132700846" } },
  "investment-property": { ids: { en: "262070978766875", zh: "261302715359051" } },
  "individual-tax-return": { ids: { en: "262071163745859", zh: "261302734330041" } },
};
const POINT_ICONS = [Clock, Lock, ShieldCheck];

export default function EmbeddedFormPage({ formKey }) {
  const { t, lang } = useLang();
  const a = t.forms[formKey];
  const goTo = useGoTo();
  useHashScroll();
  const FORM_ID = FORMS[formKey].ids[lang];
  const FORM_URL = `https://form.jotform.com/${FORM_ID}`;

  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js";
    s.async = true;
    s.onload = () => {
      if (window.jotformEmbedHandler) {
        window.jotformEmbedHandler(`iframe[id='JotFormIFrame-${FORM_ID}']`, "https://form.jotform.com/");
      }
    };
    document.body.appendChild(s);
    return () => s.remove();
  }, [FORM_ID]);

  return (
    <div data-testid={`form-page-${formKey}`} className="bg-cream-50 text-ink-900 min-h-screen">
      <Header />
      <main>
        <section className="pt-36 sm:pt-44 pb-12 bg-cream-100 relative">
          <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
          <div className="container-x relative">
            <button
              data-testid="authorisation-back"
              onClick={() => goTo("/resources")}
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-600 hover:text-brand-600 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {a.back}
            </button>
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-5">
                  <span className="hairline" />
                  <span className="text-overline">{a.overline}</span>
                </div>
                <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-ink-900 tracking-tight leading-[1.05]">
                  {a.h1}
                </h1>
                <p className="mt-6 text-base sm:text-lg text-ink-500 leading-relaxed max-w-2xl">{a.sub}</p>
              </div>
              <ul className="lg:col-span-4 space-y-3">
                {a.points.map((p, i) => {
                  const Icon = POINT_ICONS[i];
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm text-ink-800">
                      <Icon className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-white border-t border-ink-900/8 py-12 sm:py-16">
          <div className="container-x">
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute -inset-3 border-2 border-brand-500 hidden sm:block" style={{ transform: "translate(12px,12px)" }} />
              <div className="relative bg-white border border-ink-900/8 shadow-lg p-2 sm:p-4">
                {/* JotForm's embed handler re-parents the iframe; keep it inside a React-owned wrapper so unmount/remount is safe */}
                <div key={FORM_ID} data-testid="authorisation-iframe-wrap">
                  <iframe
                    id={`JotFormIFrame-${FORM_ID}`}
                    data-testid="authorisation-iframe"
                    title={a.h1}
                    src={FORM_URL}
                    allow="geolocation; microphone; camera; fullscreen; payment"
                    allowFullScreen
                    style={{ minWidth: "100%", maxWidth: "100%", height: 2200, border: "none" }}
                    scrolling="no"
                  />
                </div>
              </div>
            </div>
            <p className="mt-10 text-xs text-ink-500 text-center max-w-2xl mx-auto leading-relaxed">
              {a.fallback}{" "}
              <a href={FORM_URL} target="_blank" rel="noreferrer" data-testid="authorisation-open-external" className="text-brand-600 font-semibold hover:underline">
                {a.fallbackLink}
              </a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
